import React from 'react';
import { User, Mail, Phone, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFetchClientContacts } from '@/hooks/useClients';

interface ContactsTabProps {
  clientId: string;
}

export const ContactsTab: React.FC<ContactsTabProps> = ({ clientId }) => {
  const { data: contacts = [], isLoading, error } = useFetchClientContacts(clientId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive py-4">Failed to load contacts.</p>;
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-8 space-y-1">
        <User className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No contacts linked to this client.</p>
        <p className="text-xs text-muted-foreground">Add contacts to track key people at this organization.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {contacts.map((contact) => (
        <div
          key={contact.id}
          className="bg-surface-1 border border-border rounded p-3 flex items-start gap-3"
        >
          <div className="h-9 w-9 rounded bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
            <User className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-heading uppercase tracking-wider text-foreground truncate">
              {contact.name || 'Unnamed Contact'}
            </div>
            {contact.title && (
              <div className="text-xs text-muted-foreground truncate">{contact.title}</div>
            )}
            <div className="flex flex-col gap-0.5 mt-1.5">
              {contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors truncate"
                >
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  {contact.email}
                </a>
              )}
              {contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  {contact.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
